/**
 * Catálogo de Aplicaciones Manuales
 *
 * Administra qué aplicaciones aparecen en el dropdown de la Boleta Manual.
 * Son SOLO las que no se hacen en las cédulas de aplicación y que no están
 * amarradas a ninguna fecha ancla (Forzamiento, Maduración, Herbicida, Cura,
 * Derriba). Tabla: app_aplicaciones_manuales.
 *
 * Regla de negocio: las INACTIVAS se pueden ver (filtro) pero no editar —
 * solo reactivar. Así el histórico de boletas que las referencia queda
 * estable y no se cambia el nombre de algo bajo el que ya se registró.
 */
import React, { useCallback, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { Plus, Pencil, RefreshCw, X, Save, Eye, EyeOff, ClipboardList } from 'lucide-react';
import { supabase } from '../../supabase';
import { useEmpresaId } from '../../context/EmpresaContext';
import { useTableSort, sortRows, SortableTh } from '../../utils/tableSort';
import {
  inputCls, labelCls, btnPrimary, btnSecondary,
  pageTitleCls, pageSubtitleCls,
  tableWrapCls, theadCls, thCls, trCls, tdCls, errorCls,
} from '../../components/ui';

// ─── Tipos ────────────────────────────────────────────────────────────────────

interface AplicacionManual {
  id: number;
  nombre: string;
  orden: number;
  activo: boolean;
}

// ─── Toast ────────────────────────────────────────────────────────────────────

function Toast({ msg, ok, onClose }: { msg: string; ok: boolean; onClose: () => void }) {
  useEffect(() => {
    const t = setTimeout(onClose, 3500);
    return () => clearTimeout(t);
  }, [onClose]);
  return ReactDOM.createPortal(
    <div className="fixed bottom-5 right-5 z-50 flex items-center gap-3 px-4 py-3 rounded-xl shadow-2xl text-sm font-medium"
      style={{
        background: ok ? 'var(--tone-success-bg)' : 'var(--tone-warn-bg)',
        border: `1px solid ${ok ? 'var(--tone-success-border)' : 'var(--tone-warn-border)'}`,
        color: ok ? 'var(--tone-success-text)' : 'var(--tone-warn-text)',
      }}>
      {msg}
      <button onClick={onClose}><X size={14} /></button>
    </div>,
    document.body
  );
}

// ─── Modal alta / edición ─────────────────────────────────────────────────────

function ModalAplicacion({
  registro, empresaId, ordenSugerido, onCerrar, onGuardado,
}: {
  registro: AplicacionManual | null;   // null = alta
  empresaId: number;
  ordenSugerido: number;
  onCerrar: () => void;
  onGuardado: (msg: string) => void;
}) {
  const [nombre, setNombre] = useState(registro?.nombre ?? '');
  const [orden, setOrden]   = useState(String(registro?.orden ?? ordenSugerido));
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onCerrar(); };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [onCerrar]);

  async function guardar() {
    const nom = nombre.trim();
    if (!nom) { setError('El nombre es obligatorio.'); return; }
    const ord = Number(orden);
    if (!Number.isFinite(ord)) { setError('El orden debe ser un número.'); return; }

    setGuardando(true);
    setError('');

    const payload = { nombre: nom, orden: ord };
    const { error: e } = registro
      ? await supabase.from('app_aplicaciones_manuales').update(payload).eq('id', registro.id)
      : await supabase.from('app_aplicaciones_manuales').insert([{ ...payload, empresa_id: empresaId, activo: true }]);

    setGuardando(false);

    if (e) {
      // 23505 = unique_violation (empresa_id, nombre)
      setError(e.code === '23505'
        ? `Ya existe una aplicación llamada "${nom}".`
        : `Error al guardar: ${e.message}`);
      return;
    }
    onGuardado(registro ? 'Aplicación actualizada' : 'Aplicación creada');
  }

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.75)' }}
      onClick={onCerrar}>
      <div className="rounded-xl shadow-2xl w-full max-w-md overflow-hidden"
        style={{ background: 'var(--surface-overlay)', border: '1px solid var(--line)' }}
        onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3"
          style={{ borderBottom: '1px solid var(--line)', background: 'var(--surface-raised)' }}>
          <span className="font-semibold text-sm" style={{ color: 'var(--ink)' }}>
            {registro ? 'Editar aplicación' : 'Nueva aplicación'}
          </span>
          <button onClick={onCerrar} style={{ color: 'var(--ink-muted)' }}><X size={16} /></button>
        </div>

        {/* Body */}
        <div className="p-4 space-y-3">
          <div>
            <label className={labelCls}>Nombre</label>
            <input className={inputCls} value={nombre} autoFocus
              onChange={e => setNombre(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') guardar(); }}
              placeholder="ej. Herbicida" />
          </div>
          <div>
            <label className={labelCls}>Orden</label>
            <input className={inputCls} type="number" value={orden}
              onChange={e => setOrden(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') guardar(); }} />
            <p className="text-[11px] mt-1" style={{ color: 'var(--ink-faint)' }}>
              Define la posición en el dropdown de la Boleta Manual.
            </p>
          </div>
          {error && <p className={errorCls}>{error}</p>}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 px-4 py-3"
          style={{ borderTop: '1px solid var(--line)', background: 'var(--surface-raised)' }}>
          <button onClick={onCerrar} className={btnSecondary}>Cancelar</button>
          <button onClick={guardar} disabled={guardando} className={btnPrimary}>
            <span className="flex items-center gap-1.5">
              <Save size={13} />
              {guardando ? 'Guardando…' : 'Guardar'}
            </span>
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ─── Página ───────────────────────────────────────────────────────────────────

export function AplicacionesManualesPage() {
  const empresaId = useEmpresaId();

  const [filas, setFilas]       = useState<AplicacionManual[]>([]);
  const [loading, setLoading]   = useState(true);
  const [verInactivas, setVerInactivas] = useState(false);
  const [modal, setModal]       = useState<{ registro: AplicacionManual | null } | null>(null);
  const [toast, setToast]       = useState<{ msg: string; ok: boolean } | null>(null);

  const sort = useTableSort<'nombre' | 'orden' | 'activo'>({ key: 'orden', direction: 'asc' });

  const cargar = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('app_aplicaciones_manuales')
      .select('id, nombre, orden, activo')
      .eq('empresa_id', empresaId)
      .order('orden');
    if (error) setToast({ msg: `Error al cargar: ${error.message}`, ok: false });
    setFilas((data ?? []) as AplicacionManual[]);
    setLoading(false);
  }, [empresaId]);

  useEffect(() => { cargar(); }, [cargar]);

  async function cambiarActivo(f: AplicacionManual, activo: boolean) {
    const { error } = await supabase
      .from('app_aplicaciones_manuales')
      .update({ activo })
      .eq('id', f.id);
    if (error) {
      setToast({ msg: `Error: ${error.message}`, ok: false });
      return;
    }
    setToast({ msg: activo ? `"${f.nombre}" activada` : `"${f.nombre}" desactivada`, ok: true });
    cargar();
  }

  // Las inactivas solo se listan cuando el filtro está encendido.
  const visibles = verInactivas ? filas : filas.filter(f => f.activo);
  const nInactivas = filas.filter(f => !f.activo).length;

  const ordenadas = React.useMemo(
    () => sortRows(visibles, sort.value, (f, k) => {
      switch (k) {
        case 'nombre': return f.nombre;
        case 'orden':  return f.orden;
        case 'activo': return f.activo ? 1 : 0;
        default:       return null;
      }
    }),
    [visibles, sort.value]
  );

  const ordenSugerido = filas.length > 0 ? Math.max(...filas.map(f => f.orden)) + 1 : 1;

  return (
    <div className="p-4 space-y-4">

      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className={pageTitleCls}>Aplicaciones Manuales</h1>
          <p className={pageSubtitleCls}>
            Catálogo del dropdown de la Boleta Manual — aplicaciones fuera de cédula, sin fecha ancla
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setVerInactivas(v => !v)}
            className={btnSecondary}
            title={verInactivas ? 'Ocultar inactivas' : 'Mostrar inactivas'}>
            <span className="flex items-center gap-1.5">
              {verInactivas ? <EyeOff size={13} /> : <Eye size={13} />}
              {verInactivas ? 'Ocultar inactivas' : `Ver inactivas${nInactivas > 0 ? ` (${nInactivas})` : ''}`}
            </span>
          </button>
          <button onClick={cargar} disabled={loading} className={btnSecondary}>
            <span className="flex items-center gap-1.5">
              <RefreshCw size={13} className={loading ? 'animate-spin' : ''} />
              Actualizar
            </span>
          </button>
          <button onClick={() => setModal({ registro: null })} className={btnPrimary}>
            <span className="flex items-center gap-1.5">
              <Plus size={13} />
              Nueva aplicación
            </span>
          </button>
        </div>
      </div>

      {/* Tabla */}
      <div className={tableWrapCls}>
        <table className="w-full">
          <thead className={theadCls}>
            <tr>
              <th className={thCls}><SortableTh sort={sort} sortKey="orden" align="left">Orden</SortableTh></th>
              <th className={thCls}><SortableTh sort={sort} sortKey="nombre" align="left">Nombre</SortableTh></th>
              <th className={thCls}><SortableTh sort={sort} sortKey="activo" align="left">Estado</SortableTh></th>
              <th className={thCls} style={{ width: '1%' }}>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={4} className={`${tdCls} text-center py-8`}>Cargando…</td></tr>
            ) : ordenadas.length === 0 ? (
              <tr>
                <td colSpan={4} className={`${tdCls} text-center py-8`}>
                  {filas.length === 0
                    ? 'Sin aplicaciones. Creá la primera con "Nueva aplicación".'
                    : 'Sin aplicaciones activas. Activá el filtro para ver las inactivas.'}
                </td>
              </tr>
            ) : ordenadas.map(f => (
              <tr key={f.id} className={trCls} style={{ opacity: f.activo ? 1 : 0.55 }}>
                <td className={`${tdCls} font-mono`}>{f.orden}</td>
                <td className={tdCls}>
                  <span className="font-medium" style={{ color: 'var(--ink)' }}>{f.nombre}</span>
                </td>
                <td className={tdCls}>
                  <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold"
                    style={f.activo
                      ? { background: 'var(--tone-success-bg)', border: '1px solid var(--tone-success-border)', color: 'var(--tone-success-text)' }
                      : { background: 'var(--surface-deep)', border: '1px solid var(--line)', color: 'var(--ink-faint)' }}>
                    {f.activo ? 'Activa' : 'Inactiva'}
                  </span>
                </td>
                <td className={tdCls}>
                  <div className="flex items-center gap-2 justify-end whitespace-nowrap">
                    {f.activo ? (
                      <>
                        <button
                          onClick={() => setModal({ registro: f })}
                          className="p-1.5 rounded transition-colors"
                          style={{ color: 'var(--emp-accent-txt)' }}
                          title="Editar">
                          <Pencil size={13} />
                        </button>
                        <button
                          onClick={() => cambiarActivo(f, false)}
                          className={btnSecondary}
                          title="Desactivar: deja de aparecer en la Boleta Manual">
                          Desactivar
                        </button>
                      </>
                    ) : (
                      // Inactivas: solo lectura. No se editan para no alterar el
                      // nombre bajo el que ya se registraron boletas históricas.
                      <button
                        onClick={() => cambiarActivo(f, true)}
                        className={btnSecondary}
                        title="Reactivar para poder editarla y volver a usarla">
                        Reactivar
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Nota de ayuda */}
      <div className="flex items-start gap-2 text-[11px] rounded-lg p-3"
        style={{ background: 'var(--surface-raised)', border: '1px solid var(--line-dim)', color: 'var(--ink-muted)' }}>
        <ClipboardList size={13} className="shrink-0 mt-0.5" style={{ color: 'var(--ink-faint)' }} />
        <p>
          Solo las aplicaciones <strong>activas</strong> aparecen en el dropdown de la Boleta Manual.
          Desactivar una no afecta las boletas ya emitidas. Las inactivas no se pueden editar:
          reactivalas primero si necesitás cambiarles el nombre o el orden.
        </p>
      </div>

      {modal && (
        <ModalAplicacion
          registro={modal.registro}
          empresaId={empresaId}
          ordenSugerido={ordenSugerido}
          onCerrar={() => setModal(null)}
          onGuardado={msg => { setModal(null); setToast({ msg, ok: true }); cargar(); }}
        />
      )}

      {toast && <Toast msg={toast.msg} ok={toast.ok} onClose={() => setToast(null)} />}
    </div>
  );
}
